/**
 * Keyboard shortcuts for the queue and the case page.
 *
 * A page names its keys and what they do (`useShortcuts`). Parts of the page that own an
 * action (the strip's "Confirm", the reply draft's "Copy reply") register it by name
 * with `useShortcutHandler`; the page's key map points at those names. A key does nothing
 * while focus is in a text field, a select or an editable area, while a modifier key
 * (Ctrl, Cmd, Alt) is held, or when the viewer turned shortcuts off (the `?` dialog).
 *
 * A handler that returns `false` declines the key: the browser then does what it would
 * have done (Enter on a focused button presses it). Only a key that was handled has its
 * default action prevented.
 */

import { createContext, useContext, useEffect, useRef } from "react";

import { shortcutsOn } from "@/lib/features";
import { logger } from "@/lib/logger";
import { track } from "@/lib/usage";

/**
 * The actions of the case page that parts of the page register. The `row...` actions act
 * on the row of the draft that holds the focus (ease round, 13 Oct 2026): confirm it, mark
 * it "No item", open its price, edit its quantity, open its item search. Each declines
 * the key (returns `false`) while the focus is not in a row.
 */
export type ShortcutAction =
  | "next"
  | "previous"
  | "accept"
  | "confirmCustomer"
  | "take"
  | "doneAndNext"
  | "copyReply"
  | "rowConfirm"
  | "rowNoItem"
  | "rowPrice"
  | "rowQuantity"
  | "rowPick"
  | "help";

/** Handlers registered by the parts of one page, by action name. */
export type ShortcutRegistry = Map<string, () => void | boolean>;

/** The registry of the page in view; null outside a page with shortcuts. */
export const ShortcutContext = createContext<ShortcutRegistry | null>(null);

/**
 * Dialogs that take the keys away from the page. A notification (toast) also carries
 * `role="dialog"` but says `aria-modal="false"`: it must not block the keys, or `d`,
 * `j` and `k` would do nothing for the seconds after "Matched items accepted".
 */
export const BLOCKING_DIALOG_SELECTOR =
  '[role="dialog"]:not([aria-modal="false"]), [role="alertdialog"]:not([aria-modal="false"])';

/** True while a dialog that owns the keys is open (notifications do not count). */
export function blockingDialogOpen(root: ParentNode = document): boolean {
  return root.querySelector(BLOCKING_DIALOG_SELECTOR) !== null;
}

/** True when a key press belongs to a field the person is typing in. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || typeof element.tagName !== "string") {
    return false;
  }
  const tag = element.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") {
    return true;
  }
  if (element.isContentEditable) {
    return true;
  }
  const role = typeof element.getAttribute === "function" ? element.getAttribute("role") : null;
  return role === "textbox" || role === "combobox" || role === "searchbox";
}

/** The minimal key event the matcher reads (a DOM `KeyboardEvent` fits). */
export interface ShortcutKeyEvent {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  defaultPrevented?: boolean;
  repeat?: boolean;
  target: EventTarget | null;
}

/** The action a key press stands for under `keys`, or null when it is not a shortcut. */
export function shortcutFor<A extends string>(
  event: ShortcutKeyEvent,
  keys: Readonly<Record<string, A>>,
): A | null {
  if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
    return null;
  }
  if (isTypingTarget(event.target)) {
    return null;
  }
  return Object.hasOwn(keys, event.key) ? (keys[event.key] as A) : null;
}

/** What a page does for an action; `false` declines the key and leaves it to the browser. */
export type ShortcutHandler = () => void | boolean;

/**
 * Run the action a key stands for. Returns true when a handler took the key, which is
 * then the only case where its default action is prevented. `dialogOpen` is read only
 * when a handler exists.
 */
export function runShortcut<A extends string>(
  event: ShortcutKeyEvent & { preventDefault(): void },
  keys: Readonly<Record<string, A>>,
  handlers: Partial<Record<A, ShortcutHandler>>,
  registry: ShortcutRegistry | null,
  dialogOpen: () => boolean = blockingDialogOpen,
): boolean {
  const action = shortcutFor(event, keys);
  if (!action) {
    return false;
  }
  const handler = handlers[action] ?? registry?.get(action);
  if (!handler) {
    return false;
  }
  // Never while a dialog is open: its own keys come first. Notifications do not count.
  if (dialogOpen() && action !== "help") {
    return false;
  }
  const outcome: unknown = handler();
  if (outcome === false) {
    logger.debug("[shortcuts] declined", action);
    return false;
  }
  event.preventDefault();
  logger.debug("[shortcuts]", action);
  // Moving the row focus (`j`, `k` in the queue) is not a use of a feature.
  if (action !== "down" && action !== "up") {
    track("shortcut.use", { action });
  }
  return true;
}

/**
 * Listen for the page's keys. `handlers` are read when the key is pressed, so they can
 * change on every render; an action without a handler (nor in the registry) does nothing.
 */
export function useShortcuts<A extends string>(
  keys: Readonly<Record<string, A>>,
  handlers: Partial<Record<A, ShortcutHandler>>,
  registry: ShortcutRegistry | null = null,
  enabled = true,
) {
  const latest = useRef({ keys, handlers, registry });
  useEffect(() => {
    latest.current = { keys, handlers, registry };
  });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (!shortcutsOn()) {
        return;
      }
      const { keys: map, handlers: own, registry: shared } = latest.current;
      runShortcut(event, map, own, shared);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
}

/**
 * Register what an action does on this page while the component is mounted. A handler
 * that returns `false` declines the key, as in `useShortcuts`.
 */
export function useShortcutHandler(action: ShortcutAction, handler: ShortcutHandler | null) {
  const registry = useContext(ShortcutContext);
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  const present = handler != null;
  useEffect(() => {
    if (!registry || !present) {
      return;
    }
    const call = () => latest.current?.();
    registry.set(action, call);
    return () => {
      if (registry.get(action) === call) {
        registry.delete(action);
      }
    };
  }, [registry, action, present]);
}
